import { Trans, useLingui } from '@lingui/react';
import { Table } from '@startuptools/ui/Table';
import { NumberFormat } from '@startuptools/ui/NumberFormat';
import { DateFormat } from '@startuptools/ui/DateFormat';
import { Sheet } from '@startuptools/ui/Sheet';
import { type ShareCertificate, type ShareCertificateRange } from './ShareCertificatesInner';
import { rangeCount } from '@startuptools/common/transactions';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import {
  useDocGenShareCertificateMutation,
  useStSetShareCertificateDateMutation,
} from '../../../graphql/react-operations';
import { DateTime } from 'luxon';
import { ShareCertificateTableDataDocument } from './ShareCertificatesTableData.graphql';
import { PDFIconButton } from '@startuptools/business-ui/document/PDFPreviewIconButton';
import { apiClient } from '../../../common/http';

const ShareCertificatesInnerTableRow = ({ range, ownerId }: { range: ShareCertificateRange; ownerId: string }) => {
  const companyId = useCompanyId();
  const { i18n } = useLingui();

  // eslint-disable-next-line sut/loading-mutation
  const [doExport] = useDocGenShareCertificateMutation({ awaitRefetchQueries: true });
  // eslint-disable-next-line sut/loading-mutation
  const [doSetDate] = useStSetShareCertificateDateMutation({ refetchQueries: [ShareCertificateTableDataDocument] });

  const handleExportPdf = async (docLanguage: {
    primaryLang: boolean;
    secondaryLang: boolean;
  }): Promise<{ filename: string; blob: Blob }> => {
    if (!range.shareCertificateDate) {
      await doSetDate({
        variables: { companyId, input: { id: range.transactionId, shareCertificateDate: DateTime.now().toISODate() } },
      });
    }
    const { data } = await doExport({
      variables: {
        companyId,
        transactionId: range.transactionId,
        shareholderId: ownerId,
        blockNumber: range.blockNumber,
        docLanguage: { primary_lang: docLanguage.primaryLang, secondary_lang: docLanguage.secondaryLang },
      },
    });

    if (!data) {
      throw new Error('doh');
    }

    const docGen = data.docGenShareCertificate.docGen;
    if (!docGen.docGenDetails.filename) {
      throw new Error('no filename');
    }
    const filename = docGen.docGenDetails.filename;

    const blob = await apiClient.preview2({
      filename,
      docGens: [data.docGenShareCertificate.docGen],
    });
    return { filename, blob };
  };

  return (
    <>
      <tr>
        <td align="right">
          <NumberFormat maximumFractionDigits={0} locale={i18n.locale}>
            {range.blockNumber}
          </NumberFormat>
        </td>
        <td align="right">
          <NumberFormat maximumFractionDigits={0} locale={i18n.locale}>
            {rangeCount(range.start, range.end)}
          </NumberFormat>
        </td>
        <td align="right">
          <NumberFormat maximumFractionDigits={0} locale={i18n.locale}>
            {range.start}
          </NumberFormat>
          -
          <NumberFormat maximumFractionDigits={0} locale={i18n.locale}>
            {range.end}
          </NumberFormat>
        </td>
        <td align="right">
          <DateFormat locale={i18n.locale}>{range.transactionDate}</DateFormat>
        </td>
        <td align="right">{range.shareClass.name}</td>
        <td align="right">
          {range.shareCertificateDate ? (
            <DateFormat locale={i18n.locale}>{range.shareCertificateDate}</DateFormat>
          ) : (
            <Trans id="Not issued" />
          )}
        </td>
        <td align="right">
          <PDFIconButton generatePdf={handleExportPdf} />
        </td>
      </tr>
    </>
  );
};

export const ShareCertificatesInnerTable = ({ row }: { row: ShareCertificate }) => {
  return (
    <>
      <tr>
        <td colSpan={5}>
          <Sheet variant="soft">
            <Table>
              <thead>
                <tr>
                  <th align="right">
                    <Trans id="Block Number" />
                  </th>
                  <th align="right">
                    <Trans id="Number of Shares" />
                  </th>
                  <th align="right">
                    <Trans id="Share Number" />
                  </th>
                  <th align="right">
                    <Trans id="Transaction Date" />
                  </th>
                  <th align="right">
                    <Trans id="Share Class Name" />
                  </th>
                  <th align="right">
                    <Trans id="Share Certificate Issued" />
                  </th>
                  <th align="right" />
                </tr>
              </thead>
              <tbody>
                {row.shareRanges.map(range => (
                  <ShareCertificatesInnerTableRow key={range.blockNumber} range={range} ownerId={row.shareholder.id} />
                ))}
              </tbody>
            </Table>
          </Sheet>
        </td>
      </tr>
    </>
  );
};
